import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Buildings, MapPin, ArrowRight, Briefcase, CurrencyInr } from "@phosphor-icons/react";

/**
 * Public "Roles Hiring Now" strip. Renders 3-6 latest agency-mandate
 * roles pulled from /api/public/roles-hiring. Silently hides if fewer
 * than 2 real jobs are available (avoids showing a lonely test job).
 *
 * Used on /salary-benchmark and /interview-prep to prove the platform
 * has live roles — historically doubles/triples signup conversion.
 */
export default function RolesHiringStrip({ eyebrow, headline, subhead, limit = 6 }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/public/roles-hiring?limit=${limit}`)
            .then((r) => setRows(r.data?.items || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [limit]);

    if (loading) return null;
    if (rows.length < 2) return null;   // hide when there's nothing to prove

    return (
        <section className="border-t border-b border-[#E5E5E5] bg-[#FAFAFA]"
                 data-testid="roles-hiring-strip">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2 inline-flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-splash rounded-full animate-pulse" />
                            {eyebrow || "Live roles"}
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink">
                            {headline || "Roles hiring right now."}
                        </h2>
                        {subhead && <p className="text-sm text-[#525252] mt-1 max-w-xl">{subhead}</p>}
                    </div>
                    <Link to="/candidate/signup" data-testid="roles-hiring-cta"
                          className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-ink inline-flex items-center gap-1">
                        Match me to all live roles <ArrowRight size={11} weight="bold" />
                    </Link>
                </div>

                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {rows.slice(0, limit).map((r, i) => {
                        const CardEl = r.careers_url ? Link : "div";
                        const props = r.careers_url ? { to: r.careers_url } : {};
                        return (
                            <CardEl key={r.id}
                                    data-testid={`roles-hiring-item-${i}`}
                                    {...props}
                                    className="group bg-white border border-[#E5E5E5] hover:border-ink p-4 transition-colors flex flex-col justify-between min-h-[140px]">
                                <div>
                                    <p className="font-heading text-base font-black text-ink leading-tight mb-1 group-hover:text-cobalt line-clamp-2">
                                        {r.title}
                                    </p>
                                    <p className="text-xs text-[#525252] inline-flex items-center gap-1 mb-3">
                                        <Buildings size={11} weight="fill" className="text-[#A3A3A3]" />
                                        {r.company_name}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {r.location && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-[#FAFAFA] border border-[#E5E5E5] text-[#525252]">
                                                <MapPin size={9} weight="fill" /> {r.location}
                                            </span>
                                        )}
                                        {r.seniority && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-[#FAFAFA] border border-[#E5E5E5] text-[#525252]">
                                                <Briefcase size={9} weight="fill" /> {r.seniority}
                                            </span>
                                        )}
                                        {r.salary_range && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-splash/10 border border-splash/30 text-splash font-black">
                                                <CurrencyInr size={9} weight="bold" /> {r.salary_range}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {r.careers_url && (
                                    <p className="text-[10px] font-black uppercase tracking-wider text-cobalt group-hover:text-ink mt-3 inline-flex items-center gap-1">
                                        View role <ArrowRight size={10} weight="bold" />
                                    </p>
                                )}
                            </CardEl>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
